import AddIcon from '@mui/icons-material/Add';
import {Button, MenuItem, Stack} from '@mui/material';
import {useCallback, useEffect, useMemo} from 'react';
import {useFormContext} from 'react-hook-form';
import {useTranslation} from '../i18n/hooks/useTranslation';
import {Translation} from '../i18n/Translation';
import {Form} from '../input/Form';
import {SelectInputField} from '../input/SelectInputField';
import {TextInputField} from '../input/TextInputField';
import {ComponentWithoutChildren} from '../react/types';
import {useSignupRuleCollection} from './__hooks__/useSignupRuleCollection';
import {SignupRulePrototype, SignupRulePrototypeSchema} from '#common/auth/rules';
import {SignupRuleType} from '#common/firebase/collections';
import {hasPermission} from '#common/users/permissions';
import {User} from '#common/users/types';

export interface AddSignupRuleFormProps {
  type: SignupRuleType;
  user: User;
}

const defaultValues: SignupRulePrototype = {
  description: '',
  enabled: true,
  type: 'domain',
  value: '',
};

export const AddSignupRuleForm: ComponentWithoutChildren<AddSignupRuleFormProps> = ({
  type,
  user,
}: AddSignupRuleFormProps) => {
  const collection = useSignupRuleCollection(type);
  const canCreate = useMemo(() => hasPermission(user, 'create:signup.rule'), [user]);

  const handleSubmit = useCallback(
    async (values: SignupRulePrototype) => {
      await collection.add(values);
    },
    [collection],
  );

  if (!canCreate) {
    return null;
  }

  return (
    <Form
      defaultValues={defaultValues}
      onSubmit={(values) => void handleSubmit(values)}
      schema={SignupRulePrototypeSchema}
    >
      <AddSignupRuleFormContent />
    </Form>
  );
};

const AddSignupRuleFormContent: ComponentWithoutChildren = () => {
  const {formState, reset} = useFormContext<SignupRulePrototype>();
  const {translate} = useTranslation('user');

  // We watch for the submission success to reset the form.
  useEffect(() => {
    if (formState.isSubmitSuccessful) {
      reset(defaultValues);
    }
  }, [formState.isSubmitSuccessful, reset]);

  return (
    <Stack direction="row" spacing={1} sx={{alignItems: 'flex-start', p: 2}}>
      <SelectInputField
        label={translate('user:administration.rules.field.type.label')}
        name="type"
        size="small"
        sx={{minWidth: 120}}
      >
        <MenuItem value="domain">{translate('user:administration.rules.field.type.domain')}</MenuItem>
        <MenuItem value="exact">{translate('user:administration.rules.field.type.exact')}</MenuItem>
        <MenuItem value="regex">{translate('user:administration.rules.field.type.regex')}</MenuItem>
      </SelectInputField>
      <TextInputField
        fullWidth
        label={translate('user:administration.rules.field.value.label')}
        name="value"
        size="small"
      />
      <TextInputField
        fullWidth
        label={translate('user:administration.rules.field.description.label')}
        name="description"
        size="small"
      />
      <Button startIcon={<AddIcon />} type="submit" variant="contained">
        <Translation keys="user:administration.rules.add">{(text) => <>{text}</>}</Translation>
      </Button>
    </Stack>
  );
};
